import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import {
  zCompanySize,
  zOrgType,
  zFunction,
  zIndustry,
  zPipelinesSortColumn,
  zPipelineStatus,
  zPipelineType,
  zSortDirection,
} from 'client/zod.gen';
import { zFiltersArray } from 'helpers/router';

const pipelinesSchema = z.preprocess(
  (val) => {
    // If the value is undefined, null, or an empty object,
    // return undefined to go straight to the default value.
    if (!val) return undefined;
    if (typeof val === 'object' && val && Object.keys(val).length === 0) {
      return undefined;
    }
    return val;
  },
  z
    .object({
      // The filter hook writes `company` / `is_proactive`; the older
      // `company_slug` / `proactive` entries below never matched it.
      company: zFiltersArray(),
      company_size: zFiltersArray(zCompanySize),
      company_slug: zFiltersArray(),
      is_proactive: zFiltersArray(z.union([z.literal('yes'), z.literal('no')])),
      filtersDialog: z
        .union([
          z.literal('additional_details'),
          z.literal('company'),
          z.literal('company_size'),
          z.literal('function_expertise'),
          z.literal('industry_expertise'),
          z.literal('has_completed_intro'),
          z.literal('location'),
          z.literal('pipeline_type'),
          z.literal('is_proactive'),
          z.literal('status'),
          z.literal('team_uuid'),
          z.literal('team_type'),
        ])
        .optional()
        .catch(undefined),
      function_expertise: zFiltersArray(zFunction),
      has_completed_intro: zFiltersArray(
        z.union([z.literal('yes'), z.literal('no')]),
      ),
      industry_expertise: zFiltersArray(zIndustry),
      location: zFiltersArray(),
      notes: zFiltersArray(),
      order: zSortDirection.optional().catch('desc').default('desc'),
      phrase: z.string().optional().catch(undefined),
      pipeline_type: zFiltersArray(zPipelineType),
      proactive: zFiltersArray(z.union([z.literal('yes'), z.literal('no')])),
      sort: zPipelinesSortColumn.optional().catch('created').default('created'),
      status: zFiltersArray(zPipelineStatus),
      team_type: zFiltersArray(zOrgType),
      team_uuid: zFiltersArray(),
      title: zFiltersArray(),
    })
    .optional()
    // If the search param object is undefined, zod will go straight here.
    .default({
      order: 'desc',
      sort: 'created',
      status: ['active'],
    })
    // If an uncaught error occurs during parsing, zod will go here.
    .catch({
      order: 'desc',
      sort: 'created',
      status: ['active'],
    }),
);

export type SearchSchema = z.infer<typeof pipelinesSchema>;

export const Route = createFileRoute('/_private/_route/_internal/pipelines/')({
  validateSearch: pipelinesSchema,
});
